import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ShieldCheckIcon, UserCheckIcon, FileTextIcon, LockIcon } from 'lucide-react';
const steps = [{
  icon: FileTextIcon,
  title: 'Initial Application',
  description: 'Complete the screening form with honest, detailed information about yourself and your interests.'
}, {
  icon: UserCheckIcon,
  title: 'Identity Verification',
  description: 'Provide valid identification and professional references to ensure mutual safety and trust.'
}, {
  icon: ShieldCheckIcon,
  title: 'Consultation Call',
  description: 'A brief phone conversation to discuss your experience, expectations, and answer any questions.'
}, {
  icon: LockIcon,
  title: 'Approval & Booking',
  description: 'Once approved, you will receive booking instructions and session preparation guidelines.'
}];
export function Screening() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="screening" ref={ref} className="py-24 lg:py-32 bg-onyx" data-id="element-280">
      <div className="max-w-6xl mx-auto px-6 lg:px-12" data-id="element-281">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8
      }} className="text-center mb-16" data-id="element-282">
          <p className="text-gold text-xs uppercase tracking-[0.3em] mb-4 font-cinzel" data-id="element-283">
            Client Screening
          </p>
          <h2 className="font-cormorant text-bone text-4xl lg:text-6xl font-light mb-6" data-id="element-284">
            A Careful Selection Process
          </h2>
          <p className="text-silver-muted text-lg max-w-2xl mx-auto" data-id="element-285">
            Screening ensures safety, compatibility, and the highest quality
            experiences for all parties.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-id="element-286">
          {steps.map((step, index) => {
          const Icon = step.icon;
          return <motion.div key={step.title} initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: index * 0.1
          }} className="text-center" data-id="element-287">
                <div className="inline-flex items-center justify-center w-16 h-16 border border-gold/30 mb-4" data-id="element-288">
                  <Icon size={28} className="text-gold" data-id="element-289" />
                </div>
                <h3 className="font-cormorant text-bone text-xl mb-3" data-id="element-290">
                  {step.title}
                </h3>
                <p className="text-silver-muted text-sm leading-relaxed" data-id="element-291">
                  {step.description}
                </p>
              </motion.div>;
        })}
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={isInView ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.5
      }} className="bg-ink border border-gold/20 p-8" data-id="element-292">
          <h3 className="font-cormorant text-bone text-2xl mb-4" data-id="element-293">
            What I Look For
          </h3>
          <div className="grid md:grid-cols-2 gap-6 text-silver-muted text-sm leading-relaxed" data-id="element-294">
            <div data-id="element-295">
              <p className="mb-3" data-id="element-296">
                <strong className="text-silver" data-id="element-297">Authenticity:</strong> Genuine
                interest in BDSM and personal growth, not just curiosity or
                fantasy fulfillment.
              </p>
              <p data-id="element-298">
                <strong className="text-silver" data-id="element-299">Respect:</strong> Demonstrated
                understanding of consent, boundaries, and professional protocol.
              </p>
            </div>
            <div data-id="element-300">
              <p className="mb-3" data-id="element-301">
                <strong className="text-silver" data-id="element-302">Communication:</strong> Ability
                to articulate desires, limits, and emotional needs clearly.
              </p>
              <p data-id="element-303">
                <strong className="text-silver" data-id="element-304">Commitment:</strong> Willingness
                to invest in the screening process and honor agreements.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}